import dedent from 'ts-dedent'
import {
  ColumnDataType,
  DOSourceDefinition,
  Provider,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentMetadata,
  WorkflowComponentParamType,
  WorkflowEnvVars,
  WorkflowParameterValueError,
  WorkflowParameterValueErrorType
} from '../../types'
import { DataWarehouseConnection } from '../../workflow/connections'

import {
  EnrichComponent,
  columnsToSQL,
  extractDOSourceLocation,
  outputToSQL,
  toSQLVar,
  variablesToSQL
} from './enrichcomponent'

export abstract class WeightedEnrichComponent extends EnrichComponent {
  constructor(
    metadata: WorkflowComponentMetadata,
    commandName: string,
    gridType: string | null = null
  ) {
    super(metadata, commandName, gridType)

    this.inputs.push({
      name: 'weights_table',
      type: WorkflowComponentParamType.InputTable,
      title: 'Weights table',
      description: 'Source for the weights',
      placeholder: 'FQN of the weights table'
    })

    this.inputs.push({
      name: 'weights_col',
      type: WorkflowComponentParamType.Column,
      title: 'Weights geo column',
      parent: 'weights_table',
      dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
    })

    this.inputs.push({
      name: 'weights',
      type: WorkflowComponentParamType.SelectColumnAggregation,
      title: 'Weights column',
      parent: 'weights_table',
      noDefault: true,
      doVariables: true,
      description:
        'Comma-separated list of variable names and aggregation methods (e.g. population_93405ad7, sum, population_93405ad7, avg)',
      placeholder: 'population_93405ad7, sum, population_93405ad7, avg',
      optional: false
    })

    this.supportedProviders = [Provider.BigQuery, Provider.Snowflake]
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams): WorkflowParameterValueError[] {
    let errors = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })

    const preparedParams = this._prepareParameters(resolvedParamValues)

    if (preparedParams.weights == null) {
      errors.push({
        parameter: 'weights',
        type: WorkflowParameterValueErrorType.MissingValue,
        message: 'Weight column must be provided'
      })
    } else if ((preparedParams.weights as string).split(',').length < 2) {
      errors.push({
        parameter: 'weights',
        type: WorkflowParameterValueErrorType.WrongValue,
        message:
          'At least a variable and an aggregation method must be provided'
      })
    } else if ((preparedParams.weights as string).split(',').length > 2) {
      errors.push({
        parameter: 'weights',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'Only one weight column is allowed'
      })
    }

    return errors
  }

  protected _buildCallQuery(
    preparedParams: Record<string, unknown>,
    doSource: DOSourceDefinition | null,
    connection: DataWarehouseConnection,
    envVars: WorkflowEnvVars
  ): string {
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    const variables = preparedParams.variables as string
    const weights = preparedParams.weights as string
    const sourceTable = connection.quoteName(preparedParams.source as string)

    let weightsTable = connection.quoteName(
      preparedParams.weights_table as string
    )
    const weightsCol = preparedParams.weights_col as string

    let weightsQuery: string
    if (weightsCol === 'geom') {
      weightsQuery = `'SELECT * FROM ${weightsTable}'`
    } else {
      if (connection.type === Provider.Snowflake) {
        weightsQuery = `'SELECT * EXCLUDE(${weightsCol}), ${weightsCol} AS geom FROM ${weightsTable}'`
      } else {
        weightsQuery = `'SELECT * EXCEPT(${weightsCol}), ${weightsCol} AS geom FROM ${weightsTable}'`
      }
    }

    let query: string

    if (doSource) {
      const doSourceLocation = extractDOSourceLocation(doSource.query)
      if (weightsTable == sourceTable) {
        weightsQuery = 'NULL'
      }
      query = dedent`
        CALL ${at}.DATAOBS_${this.commandName}(
          ${this.gridType ? `'${this.gridType}',` : ''}
          ${toSQLVar('tempInputTable', connection)},
          '${preparedParams.target_col}',
          ${variablesToSQL(variables, connection)},
          NULL,
          ${variablesToSQL(weights, connection).slice(1, -1)},
          ${weightsQuery},
          NULL,
          ${outputToSQL(
            preparedParams.kring ? 'tempOutputTableKring' : 'tempOutputTable',
            connection
          )},
          '${doSourceLocation}'
        );
      `
    } else {
      query = dedent`
        CALL ${at}.${this.commandName}(
          ${this.gridType ? `'${this.gridType}',` : ''}
          ${toSQLVar('tempInputTable', connection)},
          '${preparedParams.target_col}',
          'SELECT * FROM ${sourceTable}',
          '${preparedParams.source_col}',
          ${columnsToSQL(variables, connection)},
          ${variablesToSQL(weights, connection).slice(1, -1)},
          ${weightsQuery},
          ${outputToSQL(
            preparedParams.kring ? 'tempOutputTableKring' : 'tempOutputTable',
            connection
          )}
        );
      `
    }

    query = query.replace(/^\s*[\r\n]/gm, '') // remove empty lines from the query
    return query
  }
}
